import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";

// Location Finder Import
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useGoogleMaps from "@insite/client-framework/Common/Hooks/useGoogleMaps";
import useLocationFilterSearch, { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import useLocationGoogleMarkers, {
    CurrentLocationInfoWindow,
    LocationInfoWindow,
} from "@insite/client-framework/Common/Hooks/useLocationGoogleMarkers";
import {
    getGeoCodeFromAddress,
    getGeoCodeFromLatLng,
} from "@insite/client-framework/Common/Utilities/GoogleMaps/getGeoCodeFromAddress";
import withDynamicGoogleMaps from "@insite/client-framework/Common/withDynamicGoogleMaps";
import { GetDealersApiParameter } from "@insite/client-framework/Services/DealerService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getDealersDataView,
    getDealersDefaultLocation,
} from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setParameter from "@insite/client-framework/Store/Pages/LocationFinder/Handlers/SetParameter";
import { DealerModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import LocationGoogleMap, { LocationGoogleMapStyles } from "@insite/content-library/Components/LocationGoogleMap";
import LocationSearchForm, { LocationSearchFormStyles } from "@insite/content-library/Components/LocationSearchForm";
import LocationFinderDealerDisplay, {
    LocationFinderDealerDisplayPresentationProps,
} from "@insite/content-library/Widgets/LocationFinder/LocationFinderDealerDisplay";
import ADIFindLocationModal from "@NA/Components/ADILocationFinder";
import ADILocationFinder from "@NA/Components/ADILocationFinder";
import customLoadInventory from "@NA/Handlers/GetInventory";
import LoadInventoryPuddles from "@NA/Handlers/GetInventoryPuddles";
import loadDealers from "@NA/Overrides/Store/Data/Dealers/Handlers/LoadDealers";
import customLoadDealers from "@NA/Overrides/Store/Data/Dealers/Handlers/LoadDealers";
import { getInventory } from "@NA/Services/ADIGetInventory";
import { WarehousePuddleModal } from "@NA/Services/ADIGetInventoryPuddles";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";

import { cloneDeep, isEmpty, result } from "lodash";
import SpecialOrder from "@NA/Icons/SpecialOrder";
import Typography from "@insite/mobius/Typography";
import ADIColors from "@NA/Utils/ADIColors";
import ADILBShippedByVendor from "@NA/Components/ADILBShippedByVendor";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";

interface OwnProps extends WidgetProps {
    geoLocationSearchText: string;
    showGeoLocationErrorMessage: boolean;
    onSearch: (searchGeoLocationFilter: string, searchLocationFilter: string) => void;
    geoLocationErrorMessage: React.ReactNode;
    isLoading: boolean;
    locations: LocationModel[];
    onLocationSelected: (location: LocationModel) => void;
    useLoadScript: ({ googleMapsApiKey }: { googleMapsApiKey: any }) => { isLoaded: boolean };
    nationwideHubQty: string;
}

type Props = OwnProps &
    HasProduct &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasParentProductId;

const mapStateToProps = (state: CustomApplicationState) => ({
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    productInfo: state.pages.productDetails.productInfosById,
    getDealersParameter: state.pages.locationFinder.getDealersParameter,
    settings: getSettingsCollection(state),
    session: state.context.session,
    warehouseResult: state.ADIGlobalData.wareHouseDataStore.WarehouseModel,
    homeBranch: getSettingsCollection(state) as any,
    currentShipToState: getCurrentShipToState(state).value,
    currentShipToStateIsLoading: getCurrentShipToState(state).isLoading,
    isProductLoading: state.pages.productDetails.isProductLoading,
    inventoryPuddles: state.ADIGlobalData.PdpInventoryPuddlesStore,
    adiInventory: state.ADIGlobalData.wareHouseDataStore.WarehouseModel,
    adiInventoryLoading: state.ADIGlobalData.wareHouseDataStore.isLoading,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    getInventory,
    customLoadDealers,
    customLoadInventory,
    LoadInventoryPuddles,
    loadCurrentShipTo,
};

export interface ProductDetailsAvailabilityStyles {
    availability?: ProductAvailabilityStyles;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    availabilityContainer?: InjectableCss;
    pickUpContainer?: InjectableCss;
    shipContainer?: InjectableCss;
    availableForPickUp?: InjectableCss;
    availableForPickUpText?: InjectableCss;
    availableToShip?: InjectableCss;
    availableForPickUpDetails?: InjectableCss;
    availableForPickUpStockDetails?: InjectableCss;
    availableForPickUpTextDetails?: InjectableCss;
    availableForPickUpContainer?: InjectableCss;
    pickUpStock?: InjectableCss;
    searchContainer?: InjectableCss;
    searchGeoLocationText?: InjectableCss;
    radiusBox?: InjectableCss;
    searchButton?: ButtonPresentationProps;
    lowQuantity?: InjectableCss;
    searchResultGridContainer?: GridContainerProps;
    searchResultGridItem?: GridItemProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    specialOrderText?: InjectableCss;
    specialOrderContent?: InjectableCss;
    specialOrderIconWrapper?: InjectableCss;
}

export const availabilityStyles: ProductDetailsAvailabilityStyles = {
    availabilityByWarehouseLink: {
        css: css`
            pointer-events : none;
            &:focus-visible{
                outline: none !important;
            }
            span {
                font-size: 14px;
                font-weight: normal;
                line-height: normal;
                letter-spacing: normal;
                font-family: "Nunito Sans", sans-serif;
                color: ${getColor("text.link")};
                margin:15px 0px;
                line-height: normal;
                pointer-events:auto;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css` text-decoration: underline; `,
                    css` text-decoration: underline; `,
                    css` text-decoration: underline; `,
                    null,
                    null,
                ])}
               
            }
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    searchResultGridItem: {
        width: 12,
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    searchResultGridContainer: {
        gap: 10,
    },
    searchButton: {
        css: css`
            min-width: 130px;
            border-radius: 4px !important;
            background: ${ADIColors.blue5} !important;
            min-width: 110px !important;
            height: auto !important;
            min-height: 40px;
            border: 0;
            > span {
                font-size: 16px !important;
                font-weight: bold !important;
            }
        `,
    },
    radiusBox: {
        css: css`
            width: 100%;
            min-width: 225px !important;
            font-size: 14px !important;
            font-weight: 400 !important;
            line-height: normal !important;
            padding: 9px 30px 9px 15px !important;
            border-radius: 6px !important;
        `,
    },
    searchGeoLocationText: {
        css: css`
            width: 100%;
            padding: 9px 15px 9px 19px !important;
            font-size: 14px !important;
            font-weight: 400 !important;
            line-height: 22px !important;
            border-radius: 6px !important;
        `,
    },
    searchContainer: {
        css: css`
            display: flex;
            align-items: center;
            > div[class^="FormFieldStyle"]:first-child {
                width: 40%;
                margin-right: 20px !important;
            }
            > div[class^="FormFieldStyle"]:nth-child(2) {
                width: 100%;
                max-width: 225px;
                margin-right: 20px !important;
            }
        `,
    },
    lowQuantity: {
        css: css`
            padding: 20px 15px !important;
            border: 1px solid ${getColor("common.border")};
            margin-top: 25px;
            > a {
                color: ${getColor("primary")} !important;
            }
        `,
    },
    availabilityContainer: {
        css: css`
            border-radius: 4px;
            padding: 20px 28px 28px;
        `,
    },
    pickUpContainer: {
        css: css`
            border: 1px solid ${getColor("text.disabled")};
            border-radius: 4px;
        `,
    },
    shipContainer: {
        css: css`
            border: 1px solid ${getColor("text.disabled")};
            border-radius: 4px;
            margin-top: 12px;
        `,
    },
    availableForPickUpContainer: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    availableForPickUpText: {
        css: css`
            background-color: ${getColor("text.disabled")};
            width: 100%;
            display: flex;
        `,
    },
    specialOrderText: {
        css: css`
            color: ${getColor("text.main")};
            font-size: 14px;
            font-weight: bold;
            line-height: 1.57;
            letter-spacing: normal;
            text-align: left;
            padding: 13px 0px 13px 20px;
        `,
    },
    specialOrderContent: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
            width: 100%;
            border-radius: 3px;
            border: solid 1px ${ADIColors.gray2};
            max-height: 48px;
            position: relative;
            [data-test-selector="stockAvailable"] {
                min-height: initial!important;
                //font-weight: normal;
                padding: 10px;
                /* > ${TypographyStyle} {
                    font-weight: normal;
                } */
            }
        `,
    },
    availableToShip: {
        css: css`
            display: flex;
            width: 100%;
            :nth-child(even) {
                background-color: ${getColor("common.accent")};
            }
        `,
    },
    availableForPickUpDetails: {
        css: css`
            display: flex;
            width: 100%;
            :nth-child(odd) {
                background-color: ${getColor("common.accent")};
            }
        `,
    },
    availableForPickUpStockDetails: {
        css: css`
            width: 151px;
            text-align: center;
            font-size: 14px;
            font-weight: normal;
            line-height: 30px;
            color: ${getColor("text.main")};
            text-align: center;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, css` text-align: right; padding: 0px 9px 0 0; `,], "max",);
            }}
        `,
    },

    specialOrderIconWrapper: {
        css: css`
            padding: 7.5px 20px 6.5px 20px;
            border-right: 1px solid ${ADIColors.gray2};
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                width: 13px;
                height: 13px;
            }
        `,
    },
    availableForPickUpTextDetails: {
        css: css`
            width: calc(100% - 151px);
            padding-left: 30px;
            border-right: 1px solid ${getColor("common.border")};
            font-size: 14px;
            font-weight: normal;
            line-height: 30px;
            color: ${getColor("text.main")};
            text-transform: capitalize;
            [data-test-selector="stockAvailable"] {
                min-height: initial!important;
                font-weight: normal;
                > ${TypographyStyle} {
                    font-weight: normal;
                }
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, css` border: 0;padding: 0px 10px; `,], "max",);
            }}
        `,
    },
    availableForPickUp: {
        css: css`
            width: calc(100% - 151px);
            color: ${ADIColors.black};
            font-size: 14px;
            font-weight: 600;
            line-height: normal;
            letter-spacing: normal;
            border-right: 1px solid ${getColor("common.border")};
            padding: 8px 0 7px 17px;
            background-color: ${getColor("text.disabled")};
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, css` border: 0;padding: 9px 10px; `,], "max",);
            }}
        `,
    },
    pickUpStock: {
        css: css`
            color: ${getColor("text.main")};
            font-size: 14px;
            font-weight: 600;
            line-height: normal;
            letter-spacing: normal;
            width: 151px;
            text-align: center;
            padding: 8px 0 7px 0;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, css` text-align: right; padding: 10px 9px `,], "max",);
            }}
        `,
    },
    availabilityByWarehouseModal: {
        sizeVariant: "medium",
        cssOverrides: {
            modalBody: css`
                max-width: 730px !important;
                border-radius: 4px;
                padding: 28px !important;
            `,
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                border-bottom: 0;
                padding: 0 !important;
                margin-bottom: 20px;
                > h4 {
                    font-size: 32px;
                    font-weight: bold;
                    line-height: normal;
                    color: ${ADIColors.darkBlue1};
                }
                button {
                    border-radius: 0;
                    svg {
                        height: 20px;
                    }
                }
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
};

const styles = availabilityStyles;

const StyledForm = getStyledWrapper("form");

const ProductDetailsAvailability = ({
    product,
    configurationCompleted,
    productInfo,
    geoLocationSearchText,
    getDealersParameter,
    showGeoLocationErrorMessage,
    geoLocationErrorMessage,
    isLoading,
    locations,
    settings,
    useLoadScript,
    getRealTimeWarehouseInventory,
    getInventory,
    customLoadDealers,
    customLoadInventory,
    LoadInventoryPuddles,
    session,
    warehouseResult,
    currentShipToState,
    loadCurrentShipTo,
    homeBranch,
    nationwideHubQty,
    currentShipToStateIsLoading,
    isProductLoading,
    inventoryPuddles,
    adiInventory,
    adiInventoryLoading,
}: Props) => {
    if (!product || !currentShipToState?.postalCode || inventoryPuddles.isLoading)
    {
        return null;
    }

    const { inventory, unitOfMeasure, failedToLoadInventory } = productInfo;
    const pricing = productInfo?.pricing || product.pricing;
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [inventoryResult, setInventoryResult] = React.useState<any>(null);
    const [searchGeoLocationFilter, setSearchGeoLocationFilter] = React.useState(geoLocationSearchText);
    const [warehouseInventoryDictionary, setWarehouseInventoryDictionary] = React.useState<any>({});
    const [branches, setBranches] = React.useState<any>([]);
    const [hubs, setHubs] = React.useState<any>([]);
    const [lbProductCount, setlbProductCount] = React.useState<any>([]);
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const newHomeBranch = homeBranch.maNavSettingSettings.billtoDeafultWareHouseDescription;
    const homeBranchCode = session.properties['adminDC'];
    const [displayNationwideHubQty, setDisplayNationwideHubQty] = React.useState(nationwideHubQty);

    React.useEffect(() => {
        if (
            inventoryPuddles.InventoryPuddlesData &&
            !isEmpty(inventoryPuddles.InventoryPuddlesData) //&&
            //adiInventory &&
            //!isEmpty(adiInventory)
        ) {
            puddlesCompleted(inventoryPuddles.InventoryPuddlesData);
        } 
    }, [inventoryPuddles.InventoryPuddlesData]);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const handleSearchGeoLocationChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchGeoLocationFilter(event.target.value);
    };

    const viewAvailabilityByWarehouseClickHandler = () => {
        //setModalIsOpen(true);
        // Added the below conditions from Spire Prod
        // if (adiInventory && !isEmpty(adiInventory)) {
        //     setInventoryResult(adiInventory);
        // }
        // Below commected code was in P21
        customLoadInventory({
            productId: product.productNumber,
            onSuccess(result: any) {
                setInventoryResult(adiInventory);
            },
        } as any);
        
        setModalIsOpen(true);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        getGeoCodeFromAddress(searchGeoLocationFilter)
            .then(result => {
                const geocoderResult = result[0];
            })
            .catch(error => { });

        if (searchGeoLocationFilter) {
            customLoadDealers({ ...getDealersParameter } as any);
        }
    };

    const puddlesCompleted = (data: WarehousePuddleModal) => {
        const result = cloneDeep(data);
        let hub: { location: string; qty: number; rownum: number } | undefined;
        if (result !== undefined) {
            if (result.hubs !== undefined) {
                hub = result?.hubs?.filter(h => h.location === result.regionalHubName)[0];
            }

            if (hub === undefined) {
                hub = {
                    location: result.regionalHubName,
                    qty: result.regional,
                    rownum: 0,
                };
            }

            if (result.regionalHubName) {
                const hubName =
                    result?.regionalHubName?.toUpperCase().indexOf("HUB") === -1
                        ? `${result.regionalHubName} HUB`
                        : result.regionalHubName;
                hub.location = hubName;
            }

            const resultInitialHub =
                result.hubs != null
                    ? result.hubs.filter((element, index, array) => {
                        return index <= 2 && element.qty > 0;
                    })
                    : [];

            for (const indexhub of resultInitialHub) {
                const location =
                    indexhub.location.toUpperCase().indexOf("HUB") === -1 ? `${indexhub.location} HUB` : indexhub.location;
                indexhub.location = location;
            }

            const resultHub =
                resultInitialHub != null
                    ? resultInitialHub.filter((element, index, array) => {
                        return element.location !== hub?.location;
                    })
                    : [];

            if (result.ringPuddle !== undefined && hub !== undefined) {
                result?.ringPuddle?.push(hub, resultHub[0]);
                if (resultHub[1] !== undefined) {
                    result?.ringPuddle?.push(resultHub[1]);
                }
            }

            if (isAuthenticated) {
                getHomeBranchInventory(result);
            }
        }
    };

    const getHomeBranchInventoryDetails = (result: WarehousePuddleModal) => {         
        let branches: any = [];
        let hubs: any = [];
        const lbProductCount: any = [];
        setWarehouseInventoryDictionary({});

        const homeBranch = result?.branches?.filter(b => b.location.toLowerCase().includes(newHomeBranch.toLowerCase()) && homeBranchCode === b.locationDC)[0];
        const nationalBranch = result?.branches?.filter(b => b.rownum === 0)[0];

        if (homeBranch !== undefined){
            branches.push({ 
            location: homeBranch.location, 
            qty: homeBranch.qty, 
            rownum: 0, 
            locationDC: newHomeBranch }); // In Spire Prod, have name instead of locationDC
        }
        else if (nationalBranch !== undefined && (homeBranchCode === '1110' || homeBranchCode === '3110'))
            {
            branches.push({ 
            location: nationalBranch.location, 
            qty: nationalBranch.qty, 
            rownum: nationalBranch.rownum, 
            locationDC: newHomeBranch }); // In Spire Prod, have name instead of locationDC
        }
        else{
        branches.push({
            location: newHomeBranch,
            qty: 0,
            rownum: 0,
            locationDC: newHomeBranch,  // In Spire Prod, have name instead of locationDC
        });}

        if (result.regionalHubName != null && result.regionalHubName !== undefined) {
            hubs.push({
                location:
                    result.regionalHubName.toUpperCase().indexOf("HUB") === -1
                        ? `${result.regionalHubName} HUB`
                        : result.regionalHubName,
                qty: result.regional,
                rownum: 0,
                locationDC: result.regionalHubDC,
            });
        }

        if (result.ringPuddle != null && result.ringPuddle !== undefined) {
            for (const ring of result.ringPuddle) {
                if (ring === undefined || ring?.location == null) {
                    continue;
                }
                if (
                    ring.location.toUpperCase().indexOf("HUB") === -1 &&
                    branches[0].location.toLowerCase() !== ring.location.toLowerCase() &&
                    ring.qty > 0
                ) {
                    branches.push(ring);
                } else if (
                    ring.location.toUpperCase().indexOf("HUB") > -1 &&
                    hubs[0].location.toLowerCase() !== ring.location.toLowerCase() &&
                    ring.qty > 0
                ) {
                    hubs.push(ring);
                }
            }
        }

        branches = branches.slice(0, 3);
        setBranches(branches);
        if (hubs[1] !== undefined) {
            if (hubs[1].qty === 0) {
                hubs = hubs.slice(0, 1);
            }
        }
        hubs = hubs.slice(0, 2);
        setHubs(hubs);
       setlbProductCount(result.lbProductCount);// Code from Spire Prod
        if (result.national == null || (result.national).toString() === "") { // P21 Change
            setDisplayNationwideHubQty("0");
        } else {
            setDisplayNationwideHubQty((result.national).toString());
        }
    };

    const getHomeBranchInventory = (result: WarehousePuddleModal) => {
        const currentProduct = product.productNumber;
        if (
            warehouseInventoryDictionary &&
            warehouseInventoryDictionary[currentProduct] === undefined 
                        //This below code is commented becaus we are getting adiInventory as null as Available for Pick Up and stock was getting null
            //&&
            //adiInventory &&
            //  // Added code from Spire Prod
            //!isEmpty(adiInventory) // Added code from Spire Prod
        ) {
            getHomeBranchInventoryDetails(result);
            //setInventoryResult(result);//  added code from Spire Prod
        }
    };
   
    const showAuthorizationRequired =
        (pricing &&
            pricing.additionalResults &&
            pricing.additionalResults.isVisible &&
            pricing.additionalResults.isVisible === "N")
            ? true
            : false;
            
    return (
        <>
            {isAuthenticated && pricing && pricing.additionalResults && (
                <>
                    {(((product.properties["inventoryMessage"] && product.properties["inventoryMessage"] === "Special Order") ||
                        ((product.properties.classificatioN_CODE === "S" ||
                            product.properties.classificatioN_CODE === "P" ||
                            product.properties.classificatioN_CODE === "L") &&
                            product.properties["inventoryMessageType"] === "3")) && product.properties["logicBroker"]?.toLowerCase() !== "yes") && (
                            <StyledWrapper {...styles.specialOrderContent} className="special-order-content">
                                <StyledWrapper {...styles.specialOrderIconWrapper}>
                                    <SpecialOrder />
                                </StyledWrapper>
                                <Typography {...styles.specialOrderText}>{translate("Special Order")}</Typography>
                            </StyledWrapper>
                        )}
                        
                    {(((product.properties["inventoryMessage"] && product.properties["inventoryMessage"] === "Special Order") ||
                        ((product.properties.classificatioN_CODE === "S" ||
                            product.properties.classificatioN_CODE === "P" ||
                            product.properties.classificatioN_CODE === "L") &&
                            product.properties["inventoryMessageType"] === "3")) && (product.properties["logicBroker"]?.toLowerCase() === "yes" || product.properties["lbi"]?.toLowerCase() === "yes")) && (
                            <StyledWrapper {...styles.shipContainer}>
                                <StyledWrapper>
                                    <StyledWrapper {...styles.availableForPickUpText}>
                                        <StyledWrapper {...styles.availableForPickUp}>Available to Ship</StyledWrapper>
                                        <StyledWrapper {...styles.pickUpStock}>Stock</StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper>
                                    <StyledWrapper {...styles.availableToShip}>
                                        <StyledWrapper {...styles.availableForPickUpTextDetails}>
                                            {product.brand?.name}
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.availableForPickUpStockDetails}>
                                            {lbProductCount}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}

                    {(!((product.properties["inventoryMessage"] && product.properties["inventoryMessage"] === "Special Order") ||
                        ((product.properties.classificatioN_CODE === "S" ||
                            product.properties.classificatioN_CODE === "P" ||
                            product.properties.classificatioN_CODE === "L") &&
                            product.properties["inventoryMessageType"] === "3")) && (product.properties["logicBroker"]?.toLowerCase() === "yes" || product.properties["lbi"]?.toLowerCase() === "yes" )) && (
                            <StyledWrapper {...styles.shipContainer}>
                                <StyledWrapper>
                                    <StyledWrapper {...styles.availableForPickUpText}>
                                        <StyledWrapper {...styles.availableForPickUp}>Available to Ship</StyledWrapper>
                                        <StyledWrapper {...styles.pickUpStock}>Stock</StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper>
                                    <StyledWrapper {...styles.availableToShip}>
                                        <StyledWrapper {...styles.availableForPickUpTextDetails}>
                                            {product.brand?.name}
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.availableForPickUpStockDetails}>
                                            {lbProductCount}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}

                    {!showAuthorizationRequired &&
                        (
                            <StyledWrapper
                                css={css`
                                display: flex;
                                flex-direction: column;
                                margin-top: 10px;
                                div[class^="InventoryMessage"] {
                                    background-color: transparent;
                                    padding: 0;
                                    margin-right: 18px;
                                    ${({ theme }: { theme: BaseTheme }) =>
                                        breakpointMediaQueries(theme, [
                                            css`
                                                margin-right: 7px;
                                            `,
                                            css`
                                                margin-right: 7px;
                                            `,
                                            css`
                                                margin-right: 7px;
                                            `,
                                            null,
                                            null,
                                        ])}
                                }
                            `}
                            >
                                {product.properties["logicBroker"]?.toLowerCase() !== "yes" && product.properties["lbi"]?.toLowerCase() !== "yes" && 
                                // product.properties["inventoryMessage"] !== "Special Order"  && (
                                // Change for inventory table for Special order product P21 change -- smrati
                                (product.properties.classificatioN_CODE !== "S" && product.properties.classificatioN_CODE !== "P" && product.properties.classificatioN_CODE !== "L" ) && (
                                    <StyledWrapper {...styles.pickUpContainer}>
                                        <StyledWrapper>
                                            <StyledWrapper {...styles.availableForPickUpText}>
                                                <StyledWrapper {...styles.availableForPickUp}>
                                                    {translate("Available for Pick Up")}
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.pickUpStock}>Stock</StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        
                                        {branches &&
                                            branches.map((branch: any) => {                                                
                                                return (
                                                    <StyledWrapper key={branch.location} {...styles.availableForPickUpDetails}>
                                                        <StyledWrapper {...styles.availableForPickUpTextDetails}>
                                                            {branch.location.toLowerCase()}
                                                        </StyledWrapper>
                                                        <StyledWrapper {...styles.availableForPickUpStockDetails}>
                                                            {branch.qty}
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                );
                                            })}
                                    </StyledWrapper>
                                )}
                                {product.properties["logicBroker"]?.toLowerCase() !== "yes" && product.properties["lbi"]?.toLowerCase() !== "yes" && 
                                // product.properties["inventoryMessage"] !== "Special Order" &&  
                                // Change for inventory table for Special order product P21 change -- smrati
                                (product.properties.classificatioN_CODE !== "S" && product.properties.classificatioN_CODE !== "P" && product.properties.classificatioN_CODE !== "L" ) && (
                               <StyledWrapper {...styles.shipContainer}>
                                        <StyledWrapper>
                                            <StyledWrapper {...styles.availableForPickUpText}>
                                                <StyledWrapper {...styles.availableForPickUp}>Available to Ship</StyledWrapper>
                                                <StyledWrapper {...styles.pickUpStock}>Stock</StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        <StyledWrapper>
                                            {hubs &&
                                                hubs.map((item: any) => (
                                                    <StyledWrapper key={item} {...styles.availableToShip}>
                                                        <StyledWrapper {...styles.availableForPickUpTextDetails}>
                                                            {item.location.toLowerCase()}
                                                        </StyledWrapper>
                                                        <StyledWrapper {...styles.availableForPickUpStockDetails}>
                                                            {item.qty}
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                ))}
                                            <StyledWrapper {...styles.availableToShip}>
                                                <StyledWrapper {...styles.availableForPickUpTextDetails}>
                                                    {translate("Nationwide")}
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.availableForPickUpStockDetails}>
                                                    {displayNationwideHubQty}

                                                </StyledWrapper>
                                            </StyledWrapper>

                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                                
                                {product.properties["logicBroker"]?.toLowerCase() !== "yes" && product.properties["lbi"]?.toLowerCase() !== "yes" &&
                                // product.properties["inventoryMessage"] !== "Special Order"
                                // Change for inventory table for Special order product P21 change -- smrati
                                (product.properties.classificatioN_CODE !== "S" && product.properties.classificatioN_CODE !== "P" && product.properties.classificatioN_CODE !== "L" ) && ( 
                                    <Link
                                        {...styles.availabilityByWarehouseLink}
                                        onClick={viewAvailabilityByWarehouseClickHandler}
                                    >
                                        Check Stock Availability
                                    </Link>
                                )}
                                <Modal
                                    {...styles.availabilityByWarehouseModal}
                                    headline={translate("Stock Availability")}
                                    isOpen={modalIsOpen}
                                    handleClose={modalCloseHandler}
                                >
                                    <ADILocationFinder
                                        productId={product.productNumber}
                                        warehouseDetails={inventoryResult}
                                    />
                                </Modal>
                            </StyledWrapper>
                        )}
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsAvailability)),
    definition: {
        displayName: "Availability",
        group: "Product Details",
        //allowedContexts: ["ProductDetailsPage",],
    },
};

export default widgetModule;
